import type { FC } from "react";
import { useEffect } from "react";

import { PublicationHistory } from "components/materials/section/publication/PublicationHistory";
import { PublicationReview } from "components/materials/section/publication/PublicationReview";
import { PublicationStatus } from "components/server-types";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { ArrowUpToLineIcon, LoadingDotsIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  publicationSelectors,
  selectCurrentSectionId,
  selectLatestPublicationForCurrentSection,
  selectLinkedSectionsForCurrentSection,
  selectTemplateSectionIdForCurrentSection,
} from "store/selectors";
import { useHasItemsSelector, useListSelector, useObjectSelector } from "store/store-hooks";

export const SectionPublishingPanel: FC = () => {
  const latestPublication = useObjectSelector(selectLatestPublicationForCurrentSection, [
    "id",
    "status",
  ]);
  const hasLinkedSections = useHasItemsSelector(selectLinkedSectionsForCurrentSection);
  const isDraft = !latestPublication || latestPublication.status === PublicationStatus.DRAFT;
  const hasDraftPublication = isDraft && !!latestPublication;

  const sectionId = useAppSelector(selectCurrentSectionId);
  // Publications only exist on template sections. For linked sections this is the
  // template id; for templates it's the section id. Fall back to sectionId for unlinked.
  const templateSectionId = useAppSelector(selectTemplateSectionIdForCurrentSection) ?? sectionId;

  // Full publication objects: PublicationHistory/PublicationRecord consume published_at, user_name, etc.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const sectionPublications = useListSelector((state) =>
    publicationSelectors.filterBySectionId(state, templateSectionId),
  );
  const completedPublications = sectionPublications.filter(
    (p) => p.status === PublicationStatus.COMPLETED,
  );

  useEffect(() => {
    if (templateSectionId) {
      storeApi.publications.list({ section_id: templateSectionId });
    }
  }, [templateSectionId]);

  const isPublishInProgress = latestPublication?.status === PublicationStatus.STARTED;
  const showReview = hasDraftPublication && hasLinkedSections;
  const showHistory = completedPublications.length > 0;

  return (
    <div className="mt-2">
      {showReview && latestPublication && (
        <PublicationReview latestPublicationId={latestPublication.id} />
      )}

      {isPublishInProgress && !showHistory && (
        <div className="flex justify-center py-8">
          <LoadingDotsIcon />
        </div>
      )}

      {showHistory && (
        <PublicationHistory
          className={showReview ? "mt-8" : undefined}
          completedPublications={completedPublications}
        />
      )}

      {!showReview && !isPublishInProgress && !showHistory && (
        <EmptyCard
          className="rounded-[8px] supports-squircle:rounded-[16px] supports-squircle:corner-squircle"
          description={t("publishing.no_data.description")}
          icon={<ArrowUpToLineIcon className="text-blue" />}
          title={t("publishing.no_data.title")}
        />
      )}
    </div>
  );
};
